import React, { useRef, useState, useEffect, useCallback } from "react";
import { Camera, Plus, Trash2, ArrowUp, ArrowDown, Printer, Share2, Download, Loader2, Image as ImageIcon, FileText, Crop, Contrast, Save, Star, X, FolderOpen } from "lucide-react";
import jsPDF from "jspdf";
import { toast } from "sonner";
import CropEditor from "./CropEditor";
import { helperSaveSigned, helperSignedFolders } from "../lib/api";

const BRAND = "Powered by A V Tech";

function toBW(dataUrl) {
  return new Promise((res) => {
    const im = new Image();
    im.onload = () => {
      const c = document.createElement("canvas"); c.width = im.width; c.height = im.height;
      const ctx = c.getContext("2d"); ctx.drawImage(im, 0, 0);
      const d = ctx.getImageData(0, 0, c.width, c.height); const p = d.data;
      const contrast = 1.6, mid = 128;
      for (let i = 0; i < p.length; i += 4) {
        let g = 0.299 * p[i] + 0.587 * p[i + 1] + 0.114 * p[i + 2];
        g = (g - mid) * contrast + mid + 18;
        g = Math.max(0, Math.min(255, g));
        p[i] = p[i + 1] = p[i + 2] = g;
      }
      ctx.putImageData(d, 0, 0);
      res(c.toDataURL("image/jpeg", 0.9));
    };
    im.src = dataUrl;
  });
}

export default function SnapDoc({ userId }) {
  const [pages, setPages] = useState([]); // { id, dataUrl, name }
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [bw, setBw] = useState(false);
  const [cropIdx, setCropIdx] = useState(-1);
  const [showSave, setShowSave] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");
  const [saveFolder, setSaveFolder] = useState("");
  const [saveImportant, setSaveImportant] = useState(false);
  const [saving, setSaving] = useState(false);
  const [folders, setFolders] = useState([]);
  const camRef = useRef(null);
  const fileRef = useRef(null);

  const loadFolders = useCallback(async () => { try { const r = await helperSignedFolders(userId); setFolders(r.folders || []); } catch (e) {} }, [userId]);
  useEffect(() => { if (showSave) loadFolders(); }, [showSave, loadFolders]);

  const addFiles = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => (f.type || "").startsWith("image/"));
    if (!files.length) return;
    const read = (f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res({ id: `${Date.now()}-${Math.random()}`, dataUrl: r.result, name: f.name || "page.jpg" }); r.readAsDataURL(f); });
    const added = await Promise.all(files.map(read));
    setPages((p) => [...p, ...added]);
    if (camRef.current) camRef.current.value = "";
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePage = (id) => setPages((p) => p.filter((x) => x.id !== id));
  const move = (i, d) => setPages((p) => { const a = [...p]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; });
  const applyCrop = (newUrl) => { setPages((p) => p.map((x, i) => i === cropIdx ? { ...x, dataUrl: newUrl } : x)); setCropIdx(-1); toast.success("Page cropped"); };

  const loadImg = (src) => new Promise((res, rej) => { const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = src; });

  const buildPdf = async () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight();
    const M = 28, footer = 26;
    for (let i = 0; i < pages.length; i++) {
      if (i > 0) doc.addPage();
      const url = bw ? await toBW(pages[i].dataUrl) : pages[i].dataUrl;
      const img = await loadImg(url);
      const maxW = W - 2 * M, maxH = H - 2 * M - footer;
      let iw = img.width, ih = img.height; const s = Math.min(maxW / iw, maxH / ih); iw *= s; ih *= s;
      const fmt = url.includes("image/png") ? "PNG" : "JPEG";
      doc.addImage(url, fmt, (W - iw) / 2, M + (maxH - ih) / 2, iw, ih);
      doc.setFontSize(8); doc.setTextColor(150);
      doc.text(BRAND, W / 2, H - 14, { align: "center" });
      doc.setTextColor(0);
    }
    const fname = ((title || "SnapDoc").replace(/[^a-z0-9]+/gi, "_").slice(0, 50)) + ".pdf";
    return { doc, fname };
  };

  const guard = () => { if (!pages.length) { toast.message("Add at least one page first (tap Scan / Add photo)."); return false; } return true; };

  const doDownload = async () => { if (!guard()) return; setBusy(true); try { const { doc, fname } = await buildPdf(); doc.save(fname); toast.success("PDF downloaded"); } catch (e) { toast.error("Could not build the PDF"); } finally { setBusy(false); } };

  const doShare = async () => {
    if (!guard()) return; setBusy(true);
    try {
      const { doc, fname } = await buildPdf();
      const blob = doc.output("blob");
      const file = new File([blob], fname, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], title: title || "SnapDoc", text: BRAND }); toast.success("Ready — pick Mail, WhatsApp or Messages"); return; }
        catch (e) { /* cancelled */ }
      }
      doc.save(fname); toast.message("Downloaded — attach it in your email or WhatsApp");
    } catch (e) { toast.error("Could not share"); } finally { setBusy(false); }
  };

  const doPrint = async () => {
    if (!guard()) return; setBusy(true);
    try { const { doc } = await buildPdf(); doc.autoPrint(); const url = doc.output("bloburl"); const w = window.open(url, "_blank"); if (!w) { doc.save((title || "SnapDoc") + ".pdf"); toast.message("Pop-up blocked — PDF downloaded so you can print it"); } }
    catch (e) { toast.error("Could not print"); } finally { setBusy(false); }
  };

  const downloadJpegs = async () => {
    if (!guard()) return;
    for (let i = 0; i < pages.length; i++) { const url = bw ? await toBW(pages[i].dataUrl) : pages[i].dataUrl; const a = document.createElement("a"); a.href = url; a.download = `${(title || "SnapDoc")}-${i + 1}.jpg`; a.click(); }
    toast.success("Images downloaded");
  };

  const openSave = () => { if (!guard()) return; setSaveTitle(title || "Scan " + new Date().toLocaleDateString()); setSaveFolder(""); setSaveImportant(false); setShowSave(true); };
  const confirmSave = async () => {
    setSaving(true);
    try {
      const { doc } = await buildPdf();
      const blob = doc.output("blob");
      await helperSaveSigned(userId, { title: saveTitle || "Scan", folder: saveFolder, important: saveImportant, keywords: "scan" }, blob);
      toast.success("Saved to your Signed Docs");
      setShowSave(false);
    } catch (e) { toast.error("Could not save to your docs"); }
    finally { setSaving(false); }
  };

  const inp = "w-full bg-[#0a1526] text-cyan-50 border border-[#00F0FF33] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]";
  const actBtn = "px-3 py-2 rounded-xl nx-mono text-xs font-bold flex items-center gap-1.5 border border-[#00F0FF44] text-[#00F0FF] hover:bg-[#00F0FF11] disabled:opacity-40";

  return (
    <div className="lg:col-span-2 nx-glass rounded-2xl p-4 space-y-4" data-testid="snapdoc-panel">
      <input ref={camRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={addFiles} data-testid="snapdoc-camera-input" />
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={addFiles} data-testid="snapdoc-file-input" />

      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "radial-gradient(circle at 40% 30%, #00F0FF, #061018 75%)", boxShadow: "0 0 20px #00F0FF66" }}>
          <Camera className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="nx-orbitron font-black tracking-[0.25em] text-[#00F0FF] text-base leading-none">SNAPDOC</h2>
          <p className="text-cyan-100/45 text-[10px] nx-mono mt-0.5">Powered by A V Tech</p>
        </div>
      </div>
      <p className="text-cyan-200/60 text-xs nx-mono">Snap a letter, cheque or any paper → crop it straight → then print, share, email or save it as a clean PDF.</p>

      <div>
        <label className="text-cyan-200/50 text-[11px] nx-mono">FILE NAME (optional)</label>
        <input className={inp} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Bank cheque, Sponsor letter" data-testid="snapdoc-title" />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={() => camRef.current?.click()} data-testid="snapdoc-scan-btn" className="px-4 py-2.5 rounded-xl nx-orbitron font-bold text-[#030712] bg-[#00F0FF] hover:opacity-90 flex items-center gap-2"><Camera className="w-4 h-4" /> Scan / Take photo</button>
        <button onClick={() => fileRef.current?.click()} data-testid="snapdoc-add-btn" className={actBtn}><Plus className="w-4 h-4" /> Add from gallery</button>
        <button onClick={() => setBw((v) => !v)} data-testid="snapdoc-bw-toggle" className={`px-3 py-2 rounded-xl nx-mono text-xs font-bold flex items-center gap-1.5 border ${bw ? "text-[#030712] bg-[#00F0FF] border-[#00F0FF]" : "text-[#00F0FF] border-[#00F0FF44] hover:bg-[#00F0FF11]"}`}><Contrast className="w-4 h-4" /> B&amp;W {bw ? "on" : "off"}</button>
      </div>

      {pages.length === 0 ? (
        <div className="py-14 flex flex-col items-center justify-center text-center text-cyan-100/30 gap-2 border-2 border-dashed border-[#00F0FF22] rounded-2xl">
          <ImageIcon className="w-8 h-8" />
          <p className="text-sm nx-mono">No pages yet — tap “Scan / Take photo” to capture your first page</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="snapdoc-pages">
          {pages.map((p, i) => (
            <div key={p.id} data-testid="snapdoc-page" className="relative group rounded-xl overflow-hidden border border-[#00F0FF33] bg-[#0a1526]">
              <img src={p.dataUrl} alt={`page ${i + 1}`} className="w-full h-32 object-cover" style={bw ? { filter: "grayscale(1) contrast(1.5) brightness(1.08)" } : {}} />
              <span className="absolute top-1 left-1 text-[10px] nx-mono bg-black/60 text-[#00F0FF] px-1.5 py-0.5 rounded">Page {i + 1}</span>
              <div className="absolute inset-x-0 bottom-0 flex flex-wrap justify-center gap-1 p-1 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setCropIdx(i)} data-testid="snapdoc-page-crop" title="Crop & straighten" className="p-1 rounded bg-black/50 text-cyan-100 hover:text-[#00F0FF]"><Crop className="w-3.5 h-3.5" /></button>
                <button onClick={() => move(i, -1)} data-testid="snapdoc-page-up" className="p-1 rounded bg-black/50 text-cyan-100 hover:text-[#00F0FF]"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => move(i, 1)} data-testid="snapdoc-page-down" className="p-1 rounded bg-black/50 text-cyan-100 hover:text-[#00F0FF]"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button onClick={() => removePage(p.id)} data-testid="snapdoc-page-del" className="p-1 rounded bg-black/50 text-cyan-100 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pages.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1" data-testid="snapdoc-actions">
          <button onClick={doPrint} disabled={busy} data-testid="snapdoc-print" className={actBtn}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Print</button>
          <button onClick={doShare} disabled={busy} data-testid="snapdoc-share" className="px-3 py-2 rounded-xl nx-mono text-xs font-bold flex items-center gap-1.5 text-white disabled:opacity-40" style={{ background: "linear-gradient(135deg,#25D366,#128C7E)" }}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share / Email</button>
          <button onClick={doDownload} disabled={busy} data-testid="snapdoc-download-pdf" className={actBtn}><FileText className="w-4 h-4" /> Save PDF</button>
          <button onClick={downloadJpegs} data-testid="snapdoc-download-jpeg" className={actBtn}><Download className="w-4 h-4" /> Save JPEGs</button>
          <button onClick={openSave} data-testid="snapdoc-save-docs" className="px-3 py-2 rounded-xl nx-mono text-xs font-bold flex items-center gap-1.5 text-[#030712] bg-[#00F0FF] hover:opacity-90"><Save className="w-4 h-4" /> Save to my Docs</button>
        </div>
      )}
      <p className="text-cyan-100/35 text-[10px] nx-mono">Every page carries a discreet “Powered by A V Tech” footer. On your phone, Share opens Mail, WhatsApp or Messages with the PDF already attached — forward it from there.</p>

      {cropIdx >= 0 && pages[cropIdx] && (
        <CropEditor dataUrl={pages[cropIdx].dataUrl} onApply={applyCrop} onCancel={() => setCropIdx(-1)} />
      )}

      {showSave && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="snapdoc-save-modal">
          <div className="w-full max-w-sm bg-[#071018] border border-[#00F0FF44] rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <p className="nx-orbitron font-black tracking-widest text-[#00F0FF] text-sm flex items-center gap-2"><Save className="w-4 h-4" /> SAVE TO MY DOCS</p>
              <button onClick={() => setShowSave(false)} data-testid="snapdoc-save-close" className="text-cyan-200/50 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div><label className="text-cyan-200/50 text-[11px] nx-mono">NAME</label><input className={inp} value={saveTitle} onChange={(e) => setSaveTitle(e.target.value)} data-testid="snapdoc-save-title" /></div>
            <div>
              <label className="text-cyan-200/50 text-[11px] nx-mono flex items-center gap-1"><FolderOpen className="w-3 h-3" /> FOLDER (type or pick)</label>
              <input className={inp} value={saveFolder} onChange={(e) => setSaveFolder(e.target.value)} placeholder="e.g. Bank, Personal" list="snapdoc-folder-list" data-testid="snapdoc-save-folder" />
              <datalist id="snapdoc-folder-list">{folders.map((f) => <option key={f.name} value={f.name} />)}</datalist>
            </div>
            <button onClick={() => setSaveImportant((v) => !v)} data-testid="snapdoc-save-important" className={`w-full py-2.5 rounded-xl nx-mono text-xs font-bold flex items-center justify-center gap-2 border ${saveImportant ? "text-[#030712] bg-[#F5C542] border-[#F5C542]" : "text-cyan-200/70 border-cyan-200/25 hover:border-cyan-200/50"}`}><Star className={`w-4 h-4 ${saveImportant ? "fill-[#030712]" : ""}`} /> {saveImportant ? "Marked important" : "Mark as important?"}</button>
            <p className="text-cyan-100/35 text-[10px] nx-mono text-center">Most scans aren't important — leave it off if it's routine. You'll find this in Signed Docs.</p>
            <button onClick={confirmSave} disabled={saving} data-testid="snapdoc-save-confirm" className="w-full py-3 rounded-xl nx-orbitron font-bold text-[#030712] bg-[#00F0FF] disabled:opacity-40 flex items-center justify-center gap-2">{saving ? <><Loader2 className="w-5 h-5 animate-spin" /> Saving…</> : <><Save className="w-5 h-5" /> Save</>}</button>
          </div>
        </div>
      )}
    </div>
  );
}
